import React, { useEffect, useState } from "react";
import { Row, Col, Container, Form, FormGroup, Label, Input, Button, Card, CardBody } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { baseURL } from "../../../../Services/api/baseURL";

const EMPTY = {
  latest_version: "",
  minimum_version: "",
  store_url: "",
  force_update_message: "",
  optional_update_message: "",
  up_to_date_message: "",
};


const getToken = () => JSON.parse(localStorage.getItem("token"));

const PlatformCard = ({ platform, label, form, onChange, onSave, saving }) => (
  <Card className="mb-3">
    <CardBody>
      <h6 className="fw-bold mb-3">{label}</h6>
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="text-sm">Latest version</Label>
              <Input
                value={form.latest_version}
                onChange={(e) => onChange(platform, "latest_version", e.target.value)}
                placeholder="1.4.0"
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label className="text-sm">Minimum version (force update below this)</Label>
              <Input
                value={form.minimum_version}
                onChange={(e) => onChange(platform, "minimum_version", e.target.value)}
                placeholder="1.2.0"
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label className="text-sm">Store URL</Label>
          <Input
            value={form.store_url}
            onChange={(e) => onChange(platform, "store_url", e.target.value)}
            placeholder={platform === "android"
              ? "https://play.google.com/store/apps/details?id=..."
              : "https://apps.apple.com/app/id..."}
          />
        </FormGroup>
        <FormGroup>
          <Label className="text-sm">Force-update message <small className="text-muted">(shown when below minimum version)</small></Label>
          <Input
            value={form.force_update_message}
            onChange={(e) => onChange(platform, "force_update_message", e.target.value)}
            placeholder="A required update is available. Please update to continue."
          />
        </FormGroup>

        <FormGroup>
          <Label className="text-sm">Optional-update message <small className="text-muted">(shown when a newer version exists but isn't required)</small></Label>
          <Input
            value={form.optional_update_message}
            onChange={(e) => onChange(platform, "optional_update_message", e.target.value)}
            placeholder="A new version is available."
          />
        </FormGroup>

        <FormGroup>
          <Label className="text-sm">Up-to-date message</Label>
          <Input
            value={form.up_to_date_message}
            onChange={(e) => onChange(platform, "up_to_date_message", e.target.value)}
            placeholder="App is up to date."
          />
        </FormGroup>
        <div className="text-end">
          <Button
            type="button"
            disabled={saving}
            onClick={() => onSave(platform)}
            style={{ background: "#d3178a", border: "none", borderRadius: "12px", padding: "8px 24px", fontWeight: 600 }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </Form>
    </CardBody>
  </Card>
);

export default function AppVersionManager() {
  const [forms, setForms] = useState({ android: EMPTY, ios: EMPTY });
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseURL}/api/app-version`, {
        headers: { Authorization: getToken() },
      });
      const next = { android: EMPTY, ios: EMPTY };
      (res.data?.data || []).forEach((cfg) => {
        next[cfg.platform] = {
          latest_version: cfg.latest_version || "",
          minimum_version: cfg.minimum_version || "",
          store_url: cfg.store_url || "",
          force_update_message: cfg.force_update_message || "",
          optional_update_message: cfg.optional_update_message || "",
          up_to_date_message: cfg.up_to_date_message || "",
        };
      });
      setForms(next);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const onChange = (platform, key, value) =>
    setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], [key]: value } }));

  const onSave = async (platform) => {
    const form = forms[platform];
    if (!form.latest_version || !form.minimum_version || !form.store_url) {
      Swal.fire({ title: "Latest version, minimum version and store URL are required", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    setSavingPlatform(platform);
    try {
      await axios.put(`${baseURL}/api/app-version/${platform}`, form, {
        headers: { Authorization: getToken() },
      });
      Swal.fire({ title: `${platform === "android" ? "Android" : "iOS"} config saved`, icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed to save", icon: "error", confirmButtonColor: "#d3178a" });
    } finally {
      setSavingPlatform(null);
    }
  };

  if (loading) return <p className="text-center py-5 text-muted">Loading…</p>;

  return (
    <Container fluid style={{ paddingTop: "30px" }}>
      <div className="mb-3">
        <h5 className="fw-bolder mb-0">App Version Control</h5>
        <small className="text-muted">Set minimum/latest versions per platform — no redeploy needed.</small>
      </div>
      <PlatformCard platform="android" label="Android" form={forms.android} onChange={onChange} onSave={onSave} saving={savingPlatform === "android"} />
      <PlatformCard platform="ios" label="iOS" form={forms.ios} onChange={onChange} onSave={onSave} saving={savingPlatform === "ios"} />
    </Container>
  );
}